import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Send } from "lucide-react";
import { apiFetch } from "../api/client";
import { Panel } from "../components/Panel";
import type { Conversation } from "../types";

export function ConversationDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["conversations", id],
    queryFn: () => apiFetch<{ data: Conversation }>(`/api/conversations/${id}`),
    enabled: Boolean(id),
    refetchInterval: 5000,
  });

  const sendMessage = useMutation({
    mutationFn: (body: string) =>
      apiFetch<{ data: Conversation }>(`/api/conversations/${id}/messages`, {
        method: "POST",
        body: { body },
      }),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["conversations", id] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sendMessage.isPending) return;
    sendMessage.mutate(body);
  };

  if (isLoading) return <p>Caricamento chat...</p>;

  if (isError) {
    return (
      <p className="text-red-300">
        Impossibile caricare la conversazione{error instanceof Error ? `: ${error.message}` : "."}
      </p>
    );
  }

  const conversation = data?.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/dahsbot/conversations" className="text-sm text-emerald-300 hover:text-emerald-200">
            ← Torna alle conversazioni
          </Link>
          <h2 className="mt-2 text-3xl font-semibold">
            {conversation?.lead?.profile_name ?? conversation?.lead?.phone}
          </h2>
          <p className="mt-1 text-slate-400">
            WhatsApp · {conversation?.lead?.phone ?? conversation?.lead?.whatsapp_id}
          </p>
        </div>
      </div>

      <Panel>
        <div className="space-y-4">
          {(conversation?.messages ?? []).length === 0 ? (
            <p className="text-sm text-slate-400">Nessun messaggio in questa conversazione.</p>
          ) : null}
          {conversation?.messages?.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.direction === "outbound" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-3xl rounded-2xl px-4 py-3 ${
                  message.direction === "outbound"
                    ? "bg-emerald-400 text-slate-950"
                    : "bg-slate-800 text-slate-100"
                }`}
              >
                <p className="whitespace-pre-wrap text-sm leading-6">{message.body}</p>
                <p className="mt-2 text-xs opacity-70">{message.status}</p>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel>
        <form onSubmit={submit} className="space-y-3">
          <label htmlFor="reply" className="block text-sm font-medium text-slate-300">
            Rispondi manualmente su WhatsApp
          </label>
          <textarea
            id="reply"
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Scrivi un messaggio..."
            className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-slate-100 outline-none focus:border-emerald-400"
          />
          {sendMessage.isError ? (
            <p className="text-sm text-red-300">
              {sendMessage.error instanceof Error ? sendMessage.error.message : "Invio fallito."}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={sendMessage.isPending || draft.trim() === ""}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-400 px-5 py-2.5 text-sm font-semibold text-slate-950 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {sendMessage.isPending ? "Invio..." : "Invia su WhatsApp"}
          </button>
        </form>
      </Panel>
    </div>
  );
}
